import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Share2, Copy, ArrowLeft, QrCode } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";

// Shareable QR page — user hits /share, gets a big QR pointing to the app with
// their personal referral code baked in. Screenshot it, print it, or send the link.
export default function ShareQR() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const referralCode = user?.referral_code || "";
  const shareUrl = referralCode
    ? `https://footymatch.co.uk/?ref=${referralCode}`
    : "https://footymatch.co.uk";
  const qrEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(shareUrl)}`;

  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await fetch(qrEndpoint, { credentials: "include" });
        if (!resp.ok) throw new Error(`QR endpoint returned ${resp.status}`);
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        setQrBlobUrl(objUrl);
      } catch (e) {
        toast.error(e.message || "Could not load QR code");
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [qrEndpoint]);

  const downloadQr = async () => {
    if (!qrBlobUrl) return;
    setBusy(true);
    try {
      const resp = await fetch(qrBlobUrl);
      const blob = await resp.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = "footymatch-qr.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      toast.success("Saved — print it, pin it, share it.");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally { setBusy(false); }
  };

  const shareQr = async () => {
    if (!qrBlobUrl) return;
    setBusy(true);
    try {
      const resp = await fetch(qrBlobUrl);
      const blob = await resp.blob();
      const file = new File([blob], "footymatch-qr.png", { type: "image/png" });
      const text = "Join me on Footymatch — the dating & mates app for British football fans.";
      const shareData = { title: "Footymatch", text, url: shareUrl, files: [file] };
      if (navigator.canShare && navigator.canShare(shareData)) {
        try { await navigator.share(shareData); toast.success("Shared 🎉"); return; }
        catch { /* user cancelled */ }
      }
      if (navigator.share) {
        try { await navigator.share({ title: "Footymatch", text, url: shareUrl }); toast.success("Shared 🎉"); return; }
        catch { /* fall through */ }
      }
      // No native share — fall back to download
      await downloadQr();
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied — paste it to your mates.");
        return;
      }
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Link copied."); }
      finally { ta.remove(); }
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-16 grain" data-testid="share-qr-page">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center gap-3 px-5 py-4">
          <button onClick={() => nav(-1)} data-testid="share-qr-back"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <ArrowLeft size={18} />
          </button>
          <h1 className="font-display text-2xl uppercase tracking-wider">Share Footymatch</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-6">
        <p className="text-sm text-slate-300 mb-5">
          Screenshot, print, or send this QR code. Anyone who scans it lands on Footymatch — and if you're a founding member, you'll get credit for their signup.
        </p>

        {/* The card that users will screenshot */}
        <div ref={cardRef} className="rounded-3xl p-6 relative overflow-hidden border border-white/10"
          style={{ background: "linear-gradient(160deg, #152A55 0%, #0b0f1a 60%, #d81121 130%)" }}
          data-testid="share-qr-card">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO} alt="Footymatch" className="w-11 h-11 rounded-full" />
            <div>
              <div className="font-display text-2xl uppercase tracking-wider leading-none">Footymatch</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300 mt-1">For British football fans</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 flex items-center justify-center aspect-square" data-testid="share-qr-image-wrap">
            {loading ? (
              <div className="text-slate-500 text-xs uppercase tracking-widest">Generating QR…</div>
            ) : qrBlobUrl ? (
              <img src={qrBlobUrl} alt="Scan to join Footymatch" className="w-full h-full object-contain"
                data-testid="share-qr-image" />
            ) : (
              <div className="text-slate-500 text-xs">QR unavailable</div>
            )}
          </div>

          <div className="mt-5 text-center">
            <div className="font-display text-lg uppercase tracking-wider leading-tight">Find your footy family</div>
            <div className="text-xs text-slate-300 mt-1">Love, mates, or someone to go to the game with.</div>
            {referralCode && (
              <div className="mt-3 inline-block px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] uppercase tracking-widest"
                data-testid="share-qr-ref-code">
                Invited by <span className="font-bold text-footy-red">{referralCode}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-2">
          <button onClick={shareQr} disabled={busy || loading}
            className="footy-btn-primary w-full disabled:opacity-50"
            data-testid="share-qr-share-btn">
            <Share2 size={16} /> {busy ? "Preparing…" : "Share this QR"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={downloadQr} disabled={busy || loading}
              className="footy-btn-secondary w-full disabled:opacity-50"
              data-testid="share-qr-download-btn">
              <Download size={14} /> Download
            </button>
            <button onClick={copyLink}
              className="footy-btn-secondary w-full"
              data-testid="share-qr-copy-btn">
              <Copy size={14} /> Copy link
            </button>
          </div>
        </div>

        {/* Link preview */}
        <div className="mt-5 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="share-qr-link-row">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Your share link</div>
          <div className="text-xs text-slate-200 break-all font-mono" data-testid="share-qr-link-text">{shareUrl}</div>
        </div>

        {/* Tips */}
        <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 space-y-2" data-testid="share-qr-tips">
          <div className="flex items-start gap-2">
            <QrCode size={14} className="text-footy-red mt-0.5 flex-none" />
            <span>Print it on an A5 flyer and stick it up in your pub or five-a-side changing room.</span>
          </div>
          <div className="flex items-start gap-2">
            <Share2 size={14} className="text-footy-red mt-0.5 flex-none" />
            <span>Drop it into your fan-group WhatsApp — everyone who signs up counts toward your Footymatch Passport referrals.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
